import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { ChannelHeader, InboxRow } from '../../services/api';
import type { CareTeamPatientCard } from '../../services/api/types.gen';
import { calculateAge } from '../../utils/dateUtils';
import {
  chatKeys,
  useChannel,
  useInbox,
} from '../../hooks/chat/useChatQueries';
import { NEW_MESSAGE_WINDOW } from '../../store/slices/chatWindows';

// The degraded care-team header (episode or patient not readable) has no
// patient_id or episode_id, only the fixed title the server gives it.
const CARE_TEAM_TITLE = 'Care team';

export const isCareTeam = (h: {
  patient_id?: string | null;
  title: string;
}): boolean => !!h.patient_id || h.title === CARE_TEAM_TITLE;

/**
 * Whether a conversation is one person talking to one person. The inbox row's
 * server marker wins: a shared group or care team can have one or two members
 * and still be a group. Without a row (a window restored before the inbox
 * answers), a care team is never direct and otherwise the member count decides.
 */
export function isDirectChannel(
  row: Pick<InboxRow, 'is_direct'> | undefined,
  header: ChannelHeader | undefined
): boolean {
  if (row) return !!row.is_direct;
  return (
    !!header &&
    header.kind === 'conversation' &&
    header.member_count <= 2 &&
    !isCareTeam(header)
  );
}

/**
 * Title and subtitle shared by the floating window and the phone sheet. The
 * title is never empty: until the header loads, the inbox row names it, as it
 * names the tab.
 */
export function windowTitle(
  isNew: boolean,
  header: ChannelHeader | undefined,
  inboxTitle?: string
): { title: string; subtitle: string } {
  const title = isNew
    ? 'New message'
    : header?.title || inboxTitle || 'Conversation';
  const subtitle = header
    ? header.kind === 'announcement'
      ? 'announcers only'
      : `${header.member_count} ${header.member_count === 1 ? 'member' : 'members'}`
    : '';
  return { title, subtitle };
}

/** `Female · 78 years`, `Male · 78 years`, or `78 years` without a sex. */
export function patientSubtitle(
  card: Pick<CareTeamPatientCard, 'sex' | 'date_of_birth'>
): string {
  const age = calculateAge(card.date_of_birth);
  const sex = card.sex === 'F' ? 'Female' : card.sex === 'M' ? 'Male' : null;
  const years = age === null ? null : `${age} years`;
  return [sex, years].filter(Boolean).join(' · ');
}

/**
 * The header's `last_read_seq` is what draws the "New" line, and mark-read
 * patches only the inbox row. When the surface goes away (minimize, close,
 * Back), carry that read position into the header cache, so the conversation
 * that comes back shows no "New" line for messages read before it left.
 */
export function useCarryReadPosition(id: string, skip: boolean): void {
  const queryClient = useQueryClient();
  useEffect(() => {
    if (skip) return;
    return () => {
      const read = queryClient
        .getQueryData<InboxRow[]>(chatKeys.inbox())
        ?.find(row => row.channel_id === id)?.last_read_seq;
      if (!read) return;
      queryClient.setQueryData<ChannelHeader>(chatKeys.channel(id), current =>
        current && read > (current.last_read_seq ?? 0)
          ? { ...current, last_read_seq: read }
          : current
      );
    };
  }, [id, skip, queryClient]);
}

/**
 * The header data the floating window and the phone sheet share: the channel
 * header, its patient card, and the title and subtitle. `enabled` is false
 * while the surface is not showing this conversation (the sheet passes
 * `false` while it shows the inbox); then the header is not fetched and the
 * read position is not carried. The New message window is never fetched.
 */
export function useConversationHeader(
  id: string | undefined,
  enabled: boolean
) {
  const isNew = id === NEW_MESSAGE_WINDOW;
  const active = enabled && !isNew && id !== undefined;
  // Same query key as the body's: one request, shared by the cache.
  const { data: header } = useChannel(active ? id : null);
  useCarryReadPosition(id ?? '', !active);
  const { data: inbox } = useInbox();
  const { title, subtitle } = windowTitle(
    isNew,
    header,
    inbox?.find(row => row.channel_id === id)?.title
  );
  return { header, card: header?.patient_card, title, subtitle };
}
